import { createContext, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'next-themes';
import { AuthContext } from '@reflex/ui/client/context';
import {
  useUpdateUserPreferences,
  useUserPreferencesQuery,
} from '@reflex/ui/client/user-preferences';
import { THEME_REGISTRY, DEFAULT_THEME_ID, getThemeById, type ThemeDefinition } from './themes';
import { getCustomThemes, getDefaultTheme } from './api';

const LOCAL_STORAGE_KEY = 'reflex-color-theme';

interface ColorThemeContextValue {
  themeId: string;
  setTheme: (id: string) => void;
  themes: ThemeDefinition[];
  isLoading: boolean;
}

export const ColorThemeContext = createContext<ColorThemeContextValue>({
  themeId: DEFAULT_THEME_ID,
  setTheme: () => {},
  themes: THEME_REGISTRY,
  isLoading: false,
});

function getCachedThemeId(): string {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) ?? DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

function setCachedThemeId(id: string): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, id);
  } catch {
    // localStorage unavailable
  }
}

function applyThemeVars(theme: ThemeDefinition, mode: 'light' | 'dark') {
  const vars = theme.cssVars[mode];
  const el = document.documentElement;
  for (const [key, value] of Object.entries(vars)) {
    el.style.setProperty(key, value);
  }
}

function clearThemeVars() {
  const el = document.documentElement;
  const allKeys = THEME_REGISTRY[0]?.cssVars.light;
  if (allKeys) {
    for (const key of Object.keys(allKeys)) {
      el.style.removeProperty(key);
    }
  }
}

export function useColorThemeProvider(): ColorThemeContextValue {
  const { resolvedTheme } = useTheme();
  const appliedRef = useRef(false);

  // ColorThemeProvider mounts above AuthGate, so its queries fire even while
  // the first-run setup wizard is active. The server's setup gate returns
  // 503 SETUP_REQUIRED for non-public API routes during that window, which
  // produces console-spam 503s that look like real failures. Skip the theme
  // queries until setup has finalised — the provider already falls back to
  // the cached theme id and DEFAULT_THEME_ID in the meantime. The context
  // may be null in stories that don't mount AuthProvider; treat that as
  // "no setup gating" so existing stories keep working.
  const auth = useContext(AuthContext);
  const queriesEnabled = !auth?.setupRequired;

  // Custom themes and the default are org-scoped. The selected color theme is
  // an account preference and is fetched separately through the user-scoped
  // preferences endpoint, so it remains available without an active org.
  const orgKey = auth?.activeOrganizationId ?? 'global';

  // Both queries mount globally on every cold load and only reconcile the
  // theme the provider has already applied instantly from `localStorage` (see
  // `getCachedThemeId()` below) — background work the user never awaits.
  const preferencesQuery = useUserPreferencesQuery();
  const defaultThemeQuery = useQuery({
    queryKey: ['settings', 'default-theme', orgKey],
    queryFn: getDefaultTheme,
    staleTime: Infinity,
    enabled: queriesEnabled && Boolean(auth?.activeOrganizationId),
  });

  const customThemesQuery = useQuery({
    queryKey: ['settings', 'custom-themes', orgKey],
    queryFn: getCustomThemes,
    staleTime: 5 * 60 * 1000,
    enabled: queriesEnabled,
  });

  const allThemes = useMemo(() => {
    const dbThemes: ThemeDefinition[] = (customThemesQuery.data ?? []).map((row) => ({
      id: row.name,
      label: row.label,
      accentColor: row.cssVars.dark?.['--primary'] ?? '#888',
      cssVars: row.cssVars,
    }));
    const compiledIds = new Set(THEME_REGISTRY.map((t) => t.id));
    const merged = [...THEME_REGISTRY];
    for (const t of dbThemes) {
      if (!compiledIds.has(t.id)) {
        merged.push(t);
      }
    }
    return merged;
  }, [customThemesQuery.data]);

  const cachedId = getCachedThemeId();
  const serverThemeId = preferencesQuery.data?.colorTheme;
  const resolvedThemeId = serverThemeId ?? defaultThemeQuery.data?.themeId ?? cachedId;

  const mutation = useUpdateUserPreferences();
  const mutatePreferences = mutation.mutate;

  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !preferencesQuery.isSuccess) return;
    seededRef.current = true;
    if (serverThemeId === null && cachedId !== DEFAULT_THEME_ID) {
      mutatePreferences({ colorTheme: cachedId });
    }
  }, [preferencesQuery.isSuccess, serverThemeId, cachedId, mutatePreferences]);

  const setTheme = useCallback(
    (id: string) => {
      setCachedThemeId(id);
      mutatePreferences({ colorTheme: id });
    },
    [mutatePreferences],
  );

  const findTheme = useCallback(
    (id: string): ThemeDefinition | undefined => {
      return getThemeById(id) ?? allThemes.find((t) => t.id === id);
    },
    [allThemes],
  );

  useEffect(() => {
    const mode = resolvedTheme === 'dark' ? 'dark' : 'light';
    const theme = findTheme(resolvedThemeId);

    if (!theme) {
      clearThemeVars();
      return;
    }

    applyThemeVars(theme, mode);
    appliedRef.current = true;
    setCachedThemeId(resolvedThemeId);
  }, [resolvedThemeId, resolvedTheme, findTheme]);

  // Apply cached theme immediately on first render to avoid flash
  useEffect(() => {
    if (appliedRef.current) return;
    const cachedId = getCachedThemeId();
    const theme = findTheme(cachedId);
    if (theme) {
      const mode = resolvedTheme === 'dark' ? 'dark' : 'light';
      applyThemeVars(theme, mode);
    }
  }, []);

  return {
    themeId: resolvedThemeId,
    setTheme,
    themes: allThemes,
    isLoading: preferencesQuery.isLoading || defaultThemeQuery.isLoading,
  };
}

export function useColorTheme(): ColorThemeContextValue {
  return useContext(ColorThemeContext);
}
